import React, {ChangeEvent} from 'react';
import {Select} from 'antd';
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import {ZipcodeFiltersType} from "../graphql/queries/getZipcodeListing";
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';


interface ZipcodeFilterProps {
    filters: ZipcodeFiltersType;
    setFilters: (filters: ZipcodeFiltersType) => void;
}


const ZipcodeFilter: React.FC<ZipcodeFilterProps> = ({
    filters,
    setFilters,
}) => {
    const {t} = useTranslation();
    const col1Labels = [
        t('central.zipcodes.filters.zipcode'),
        t('central.zipcodes.filters.state'),
        t('central.zipcodes.filters.city'),
        t('central.zipcodes.filters.sortingCode'),
    ];
    const col2Labels = [
        t('central.zipcodes.filters.isPickup'),
        t('central.zipcodes.filters.isDelivery'),
    ];
    const col3Labels = [
        t('central.zipcodes.filters.allowTakingOrder'),
        t('central.zipcodes.filters.isTakingDeliveryOrder'),
    ];

    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);
    const deliveryOptions = [
        { label: t('enum.yesorNoStatus.all'), value: '' },
        { label: t('enum.yesorNoStatus.yes'), value: 'true' },
        { label: t('enum.yesorNoStatus.no'), value: 'false' }
    ];
    
    const pickupOptions = [
        { label: t('enum.yesorNoStatus.all'), value: '' },
        { label: t('enum.yesorNoStatus.yes'), value: 'true' },
        { label: t('enum.yesorNoStatus.no'), value: 'false' }
    ];

    const takingOrderOptions = [
        { label: t('enum.yesorNoStatus.all'), value: '' },
        { label: t('enum.yesorNoStatus.yes'), value: 'true' },
        { label: t('enum.yesorNoStatus.no'), value: 'false' }
    ];

    const allowTakingOrderOptions = [
        { label: t('enum.yesorNoStatus.all'), value: '' },
        { label: t('enum.yesorNoStatus.yes'), value: 'true' },
        { label: t('enum.yesorNoStatus.no'), value: 'false' }
    ];
    
    const handleDeliveryChange = (value: string) => {
        setFilters({
            ...filters,
            isDelivery: value ? (value === 'true') : undefined
        });
    };

    const handlePickupChange = (value: string) => {
        setFilters({
            ...filters,
            isPickup: value ? (value === 'true') : undefined
        });
    };

    const handleTakingOrderChange = (value: string) => {
        setFilters({
            ...filters,
            isTakingDeliveryOrder: value ? (value === 'true') : undefined
        });
    };

    const handleAllowTakingOrderChange = (value: string) => {
        setFilters({
            ...filters,
            allowTakingOrder: value ? (value === 'true') : undefined
        });
    };

    return (
        <div className="mb-2 py-2">
            {/* 邮编批量输入框 - 单独一行 */}
                <div className="mb-4">
                    <BatchInputFilterItem
                        label={t('central.zipcodes.filters.zipcode')}
                        value={filters.zipcode}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                            ...filters,
                            zipcode: e.target.value || undefined
                        })}
                        spanClassName={col1SpanClassName}
                        placeholder={t('central.zipcodes.filters.zipcodePlaceholder') as string}
                        rows={2}
                    />
                </div>
            {/* 使用 Grid 布局确保列对齐 - 三等分布局 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.zipcodes.filters.state')}
                        value={filters.state}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({
                            ...filters,
                            state: e.target.value || undefined
                        })}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.zipcodes.filters.isPickup')}
                        options={pickupOptions}
                        value={filters.isPickup?.toString() ?? undefined}
                        onChange={handlePickupChange}
                        spanClassName={col2SpanClassName}
                        className="w-full"
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.zipcodes.filters.allowTakingOrder')}
                        options={allowTakingOrderOptions}
                        value={filters.allowTakingOrder?.toString() ?? undefined}
                        onChange={handleAllowTakingOrderChange}
                        spanClassName={col3SpanClassName}
                        className="w-full"
                    />
                </div>
                {/* 第二行 */}
                {/* 第一列 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.zipcodes.filters.city')}
                        value={filters.city}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({
                            ...filters,
                            city: e.target.value || undefined
                        })}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                {/* 第二列 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.zipcodes.filters.isDelivery')}
                        options={deliveryOptions}
                        value={filters.isDelivery?.toString() ?? undefined}
                        onChange={handleDeliveryChange}
                        spanClassName={col2SpanClassName}
                        className="w-full"
                    />
                </div>
                {/* 第三列 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.zipcodes.filters.isTakingDeliveryOrder')}
                        options={takingOrderOptions}
                        value={filters.isTakingDeliveryOrder?.toString() ?? undefined}
                        onChange={handleTakingOrderChange}
                        spanClassName={col3SpanClassName}
                        className="w-full"
                    />
                </div>
                {/* 第三行 */}
                {/* 第一列 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.zipcodes.filters.sortingCode')}
                        value={filters.sortingCode}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({
                            ...filters,
                            sortingCode: e.target.value || undefined
                        })}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                <div className="min-w-0"></div>
                <div className="min-w-0"></div>
            </div>
        </div>
    );
};

export default ZipcodeFilter; 